document.addEventListener("DOMContentLoaded", () => {
    const selectors = {
        cartDrawer: "cart-drawer",
        quickAdd: "quick-add-drawer",
        megaMenu: [
            '.header__menu-item[aria-expanded="true"]',
            "details.is-open"
        ],
        klaviyo: 'div[data-testid="POPUP"]',
        stickyPanel: "sticky-atc-panel",
        customSticky: ".product-info__add-to-cart",
        bundlePreview: "bundle-preview"
    };

    const body = document.body;
    const desktopMq = window.matchMedia("(min-width: 769px)");
    // The bundle preview is a bottom sheet only below 1024px (see bundle-preview.css)
    const bundleSheetMq = window.matchMedia("(max-width: 1023.98px)");
    let rafId = null;

    // Keep the last applied state so we only touch the DOM when needed
    let previousState = {
        hidden: false,
        movedUp: false,
        movedSide: false,
        bottomOffset: "",
        sideOffset: ""
    };

    function scheduleUpdate() {
        if (rafId) return;

        rafId = requestAnimationFrame(() => {
            rafId = null;
            const nextState = computeState();
            applyState(nextState);
        });
    }

    function getElements() {
        return {
            cartEl: document.querySelector(selectors.cartDrawer),
            quickAddEl: document.querySelector(selectors.quickAdd),
            stickyEl: document.querySelector(selectors.stickyPanel),
            customStickyEl: document.querySelector(selectors.customSticky),
            klaviyoEl: document.querySelector(selectors.klaviyo),
            bundleEl: document.querySelector(selectors.bundlePreview)
        };
    }

    function isVisible(el, checkOffset = false) {
        if (!el) return false;

        const style = window.getComputedStyle(el);
        return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            style.opacity !== "0" &&
            (el.offsetParent !== null || !checkOffset)
        );
    }

    function computeState() {
        const isDesktop = desktopMq.matches;
        const {
            cartEl,
            quickAddEl,
            stickyEl,
            customStickyEl,
            klaviyoEl,
            bundleEl
        } = getElements();

        // Menu open
        const isMenuOpen = selectors.megaMenu.some((sel) => document.querySelector(sel));

        // Drawer open
        const isCartOpen = !!cartEl?.hasAttribute("open");
        const isQuickAddOpen = !!quickAddEl?.hasAttribute("open");

        // Popup open
        const isKlaviyoOpen = isVisible(klaviyoEl, true);

        // Bundle preview: fixed bottom sheet below 1024px (peek bar collapsed, full sheet when open)
        const isBundleSheet = bundleSheetMq.matches && isVisible(bundleEl);
        const isBundleOpen = isBundleSheet && bundleEl.classList.contains("is-open");

        // Bottom bar detection
        let isBarVisible = false;
        let bottomBarHeight = 0;

        if (customStickyEl && customStickyEl.classList.contains("is-sticky") && isVisible(customStickyEl)) {
            isBarVisible = true;
            bottomBarHeight = customStickyEl.offsetHeight;
        } else if (stickyEl) {
            const isHidden = stickyEl.classList.contains("sticky-atc-panel--out");

            if (!isHidden && isVisible(stickyEl)) {
                isBarVisible = true;
                bottomBarHeight = stickyEl.offsetHeight + 10;
            }
        }

        // Collapsed bundle peek bar: lift the chat above the visible part of the
        // sheet (innerHeight - rect.top also covers the safe-area inset)
        if (isBundleSheet && !isBundleOpen) {
            const peekHeight = Math.max(0, Math.round(window.innerHeight - bundleEl.getBoundingClientRect().top));

            if (peekHeight > 0) {
                isBarVisible = true;
                bottomBarHeight = Math.max(bottomBarHeight, peekHeight);
            }
        }

        const nextState = {
            hidden: false,
            movedUp: false,
            movedSide: false,
            bottomOffset: "",
            sideOffset: ""
        };

        // Priority 1: hide
        if (isKlaviyoOpen || isMenuOpen) {
            nextState.hidden = true;
            return nextState;
        }

        // Expanded bundle sheet covers the screen like the mobile cart drawer
        if (isBundleOpen) {
            nextState.hidden = true;
            return nextState;
        }

        if (!isDesktop) {
            if (isCartOpen || isQuickAddOpen) {
                nextState.hidden = true;
                return nextState;
            }
        } else {
            if (isCartOpen || isQuickAddOpen) {
                const width = isCartOpen
                    ? cartEl?.getBoundingClientRect().width || 0
                    : quickAddEl?.getBoundingClientRect().width || 0;

                nextState.movedSide = width > 0;
                nextState.sideOffset = width > 0 ? `${width}px` : "";
            }
        }

        // Priority 2: move up
        if (isBarVisible && bottomBarHeight > 0) {
            nextState.movedUp = true;
            nextState.bottomOffset = `${bottomBarHeight}px`;
        }

        return nextState;
    }

    function applyState(next) {
        // If nothing changed, do nothing
        const didChange =
            next.hidden !== previousState.hidden ||
            next.movedUp !== previousState.movedUp ||
            next.movedSide !== previousState.movedSide ||
            next.bottomOffset !== previousState.bottomOffset ||
            next.sideOffset !== previousState.sideOffset;

        if (!didChange) return;

        body.classList.toggle("chat-hidden", next.hidden);
        body.classList.toggle("chat-moved-up", next.movedUp);
        body.classList.toggle("chat-moved-side", next.movedSide);

        if (next.bottomOffset) {
            body.style.setProperty("--chat-bottom-offset", next.bottomOffset);
        } else {
            body.style.removeProperty("--chat-bottom-offset");
        }

        if (next.sideOffset) {
            body.style.setProperty("--chat-side-offset", next.sideOffset);
        } else {
            body.style.removeProperty("--chat-side-offset");
        }

        previousState = next;
    }

    // -----------------------------
    // Event listeners
    // -----------------------------

    // Initial sync
    scheduleUpdate();

    // Resize can affect mobile/desktop logic and widths/heights
    window.addEventListener("resize", scheduleUpdate, { passive: true });

    // Keep this only if sticky bars depend on scroll position
    window.addEventListener("scroll", scheduleUpdate, { passive: true });

    // <details> / disclosure changes
    document.addEventListener(
        "toggle",
        (event) => {
            const target = event.target;
            if (
                target.matches?.("details") ||
                target.matches?.(selectors.cartDrawer) ||
                target.matches?.(selectors.quickAdd) ||
                target.matches?.(selectors.stickyPanel)
            ) {
                scheduleUpdate();
            }
        },
        true
    );

    // Clicks that usually open/close menus, drawers, sticky controls, etc.
    document.addEventListener(
        "click",
        (event) => {
            const trigger = event.target.closest(`
        ${selectors.cartDrawer},
        ${selectors.quickAdd},
        ${selectors.stickyPanel},
        ${selectors.customSticky},
        .header,
        .header__menu-item,
        [aria-expanded],
        ${selectors.klaviyo},
        .bundle-preview-section
      `);

            if (trigger) {
                scheduleUpdate();

                // In many themes DOM updates happen after the click handler finishes
                setTimeout(scheduleUpdate, 0);
            }
        },
        true
    );

    // When animations/transitions end, visibility/size may have changed
    document.addEventListener(
        "transitionend",
        (event) => {
            const target = event.target;
            if (
                target.matches?.(selectors.stickyPanel) ||
                target.matches?.(selectors.customSticky) ||
                target.matches?.(selectors.klaviyo) ||
                target.matches?.(selectors.cartDrawer) ||
                target.matches?.(selectors.quickAdd) ||
                target.matches?.(selectors.bundlePreview)
            ) {
                scheduleUpdate();
            }
        },
        true
    );

    document.addEventListener(
        "animationend",
        (event) => {
            const target = event.target;
            if (
                target.matches?.(selectors.stickyPanel) ||
                target.matches?.(selectors.customSticky) ||
                target.matches?.(selectors.klaviyo)
            ) {
                scheduleUpdate();
            }
        },
        true
    );

    // Bundle preview opens/closes without clicks too (swipe, Escape), so watch its class
    const bundleObserved = document.querySelector(selectors.bundlePreview);
    if (bundleObserved && window.MutationObserver) {
        new MutationObserver(scheduleUpdate).observe(bundleObserved, {
            attributes: true,
            attributeFilter: ["class"]
        });
    }

    // Media query change
    [desktopMq, bundleSheetMq].forEach((mq) => {
        if (mq.addEventListener) {
            mq.addEventListener("change", scheduleUpdate);
        } else if (mq.addListener) {
            mq.addListener(scheduleUpdate);
        }
    });
});
